
import { PropType, defineComponent} from 'vue';
import moment, { Moment } from 'moment';
import { NSpin, NTimeline, NTimelineItem } from 'naive-ui';


import componentBase from '~/components/component-base';

import { StationEvent } from '~/api/models/station-event.model';
import { Station } from '~/api/models/station.model';
import { AvoltaAlarmEnum, AvoltaAlarmKeyEnum } from '~/configs/alarms/avolta-alarm.model';
import { AvoltaAlarmConfiguration } from '~/configs/alarms/avolta-alarm.config';
import { getAlarm } from '~/misc/events-utils';
import { greyLighterColor } from '~/variables';

export type AlarmMessages = { [key in AvoltaAlarmEnum]: { recovery: string, warning: string }};

export default defineComponent({

    extends: componentBase,

    components: {
        NTimeline,
        NTimelineItem,
        NSpin,
    },

    data() {
        return {
            AvoltaAlarmEnum,
            AvoltaAlarmKeyEnum,
            moment,
        };
    },

    props: {
        station: { type: Object as PropType<Station> },

        events: { type: Object as PropType<StationEvent[]>},
        since: { type: Object as PropType<Moment>},
        before: { type: Object as PropType<Moment>},
    },

    computed: {
        timelineEvents(): StationEvent[] {

            if(!this.events) {
                return [];
            }

            return this.events
                .filter((event: StationEvent) => ['warning', 'recovery'].includes(event?.level))
                .sort((e1,e2) => e2?.date - e1?.date)
        }
    },

    methods: {
        
        getEventTitle(event: StationEvent): string {
            const alarm: AvoltaAlarmEnum = getAlarm(event);

            if(!event) {
                return '';
            }

            if(event.level === 'warning') {
                switch(alarm) {
                    case AvoltaAlarmEnum.CH4_concentration_alarm:
                        return this.$t('device_details.events.timeline.gas_warning_title');
                    case AvoltaAlarmEnum.CH4_battery_alarm:
                        return this.$t('station_details.events.timeline.battery_warning_title');
                    case AvoltaAlarmEnum.CH4_system_failure:
                        return this.$t('station_details.events.timeline.system_failure_warning_title');
                    case AvoltaAlarmEnum.CH4_eol_alarm:
                        return this.$t('station_details.events.timeline.eol_warning_title');
                    case AvoltaAlarmEnum.offline:
                        return this.$t('device_details.events.timeline.offline_warning_title');
                }
            } else if(event?.level === 'recovery') {
                switch(alarm) {
                    case AvoltaAlarmEnum.CH4_concentration_alarm:
                        return this.$t('device_details.events.timeline.gas_recovery_title');
                    case AvoltaAlarmEnum.CH4_battery_alarm:
                        return this.$t('device_details.events.timeline.battery_recovery_title');
                    case AvoltaAlarmEnum.CH4_system_failure:
                        return this.$t('device_details.events.timeline.system_failure_recovery_title');
                    case AvoltaAlarmEnum.CH4_eol_alarm:
                        return this.$t('device_details.events.timeline.eol_recovery_title');
                    case AvoltaAlarmEnum.offline:
                        return this.$t('device_details.events.timeline.offline_recovery_title');
                }
            } else {
                return '';
            }

        },

        getEventContent(event: StationEvent): string {
            const alarm: AvoltaAlarmEnum = getAlarm(event);

            if(!event) {
                return '';
            }

            if(event.level === 'warning') {
                switch(alarm) {
                    case AvoltaAlarmEnum.CH4_concentration_alarm:
                        return this.$t('device_details.events.timeline.gas_warning_content');
                    case AvoltaAlarmEnum.CH4_battery_alarm:
                        return this.$t('device_details.events.timeline.battery_warning_content');
                    case AvoltaAlarmEnum.CH4_system_failure:
                        return this.$t('device_details.events.timeline.system_failure_warning_content');
                    case AvoltaAlarmEnum.CH4_eol_alarm:
                        return this.$t('device_details.events.timeline.eol_warning_content');
                    case AvoltaAlarmEnum.offline:
                        return this.$t('device_details.events.timeline.offline_warning_content');
                }
            } else if(event?.level === 'recovery') {
                switch(alarm) {
                    case AvoltaAlarmEnum.CH4_concentration_alarm:
                        return this.$t('device_details.events.timeline.gas_recovery_content');
                    case AvoltaAlarmEnum.CH4_battery_alarm:
                        return this.$t('device_details.events.timeline.battery_recovery_content');
                    case AvoltaAlarmEnum.CH4_system_failure:
                        return this.$t('device_details.events.timeline.system_failure_recovery_content');
                    case AvoltaAlarmEnum.CH4_eol_alarm:
                        return this.$t('device_details.events.timeline.eol_recovery_content');
                    case AvoltaAlarmEnum.offline:
                        return this.$t('device_details.events.timeline.offline_recovery_content');
                }
            } else {
                return event?.message;
            }

        },

        getEventColor(event: StationEvent): string {

            if(event?.level === 'recovery') {
                return greyLighterColor;
            } 

            if(event?.level === 'info') {
                return greyLighterColor;
            }

            const alarm : AvoltaAlarmEnum = getAlarm(event);

            if(!alarm) {
                return greyLighterColor;
            }

            return AvoltaAlarmConfiguration[alarm]?.color;
        },
    }

});